/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  DatePicker,
  Modal,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { getModelPermission } from 'utils/permission';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import {
  getPrevisionesSocialesExentas,
  postPrevisionSocialExenta,
  deletePrevisionSocialExenta,
  patchPrevisionSocialExenta,
  getPrevisionSocialExenta,
} from 'api/nominas/percepciones/previsionSocialExenta';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import ValorExento, { valorExentoPermission } from './ValorExento';

export const previsionSocialExentaPermission = {
  permissionModel: 'previsionsocialexenta',
  catalogsModels: [],
  tabsModels: [],
};

const PrevisionSocialExenta = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [percepciones, setPercepciones] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);

  const formInstances = [form];

  const getValorExentoPermission = getModelPermission(
    valorExentoPermission.permissionModel,
    valorExentoPermission.catalogsModels,
    valorExentoPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPrevisionesSocialesExentas();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_estados, _percepciones, _data] = await Promise.all([
        getEstadosGlobales(),
        getPercepciones({ estados_globales: ESTATUS_AUTORIZADO }),
        getPrevisionesSocialesExentas(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPercepciones(_percepciones);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _prevision = await getPrevisionSocialExenta(match.id);
    setTimeout(() => {
      if (_prevision) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_prevision));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = getFormattedValues(form.getFieldsValue());
      const [key] = selectedRowKeys;
      const saved = await (key ? patchPrevisionSocialExenta(key, values) : postPrevisionSocialExenta(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePrevisionSocialExenta(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un registro ya autorizado');
        return;
      }
      const vals = {
        ...getFormattedValues(form.getFieldsValue()),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchPrevisionSocialExenta(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setVisibleAutorizar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const percepcionLabel = (val) => {
    const p = percepciones.find((e) => e.id === val);
    if (!p) return '';
    return `${p.clave} - ${p.descripcion}`;
  };

  const columns = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (val) => percepcionLabel(val),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? val.format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          titleTable="Previsión Social Exenta"
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableDelete={
            data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
          }
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'previsionsocialexenta', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Previsión Social Exenta`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          width="40%"
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="previsionSocialExenta"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="percepcion"
                  label="Percepción"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    keyLabelRender
                    dataSource={percepciones}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  initialValue={ESTATUS_ACTIVO}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Previsión Social Exenta ${percepcionLabel(form.getFieldValue('percepcion'))}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          open={visibleAutorizar}
          contentTitle={`Autorizar la Previsión Social Exenta ${percepcionLabel(form.getFieldValue('percepcion'))}`}
          loading={loading}
        />
      </Spin>
      <Col xs={24} sm={24} md={24} style={{ marginTop: 24 }}>
        <ValorExento permission={getValorExentoPermission} />
      </Col>
    </Row>
  );
};

export default PrevisionSocialExenta;
